/**
 * El ciclo de vida de las propuestas de movimiento (spec 015 · FR-024…FR-028).
 *
 * Vive aparte de la pantalla porque es un CICLO con estado propio —qué hay
 * pendiente, si se está guardando, qué falló— y no tiene nada que ver con pintar
 * un calendario. Mezclado con la pantalla, esas reglas se leen como detalles del
 * `onClick` de turno.
 */
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { ErrorApi } from '../lib/api'
import { patchMoverCita } from './calendario-acciones'
import { sinPropuesta, type Propuesta } from './calendario-propuestas'

export function usarPropuestas(accountId: string | undefined) {
    const qc = useQueryClient()
    const [propuestas, setPropuestas] = useState<Map<number, Propuesta>>(new Map())
    const [guardando, setGuardando] = useState(false)
    const [fallos, setFallos] = useState<Array<{ citaId: number; motivo: string }>>([])

    /**
     * Guardar las propuestas (FR-026, FR-028).
     *
     * UNA A UNA, Y CADA UNA CON SU RESULTADO. Un «no se pudo guardar» a secas con
     * cinco cambios encima obliga a recargar para saber cuáles entraron. Así que
     * la que sale bien se retira de la lista y la que falla se queda, con su
     * motivo: lo que queda en pantalla es exactamente lo que falta por hacer.
     *
     * La comprobación de si el hueco está libre y dentro del horario **la hace el
     * servidor** (`validarFranjaAgenda`, excluyendo la propia cita). No se
     * duplica aquí: dos opiniones sobre si un hueco cabe divergen, y la que manda
     * es la que escribe.
     */
    async function guardarPropuestas() {
        if (!accountId) return
        setGuardando(true)
        const nuevosFallos: Array<{ citaId: number; motivo: string }> = []
        // Se guarda lo que había AL PULSAR. Lo que se arrastre mientras tanto no
        // entra en esta tanda — y por eso, al terminar, no se puede escribir el
        // estado de golpe: se retiran UNA A UNA las que salieron bien.
        const tanda = [...propuestas.values()]
        const hechas: number[] = []

        for (const p of tanda) {
            try {
                await patchMoverCita(accountId, p.citaId, p.dia, p.inicio, p.minutos, p.profesionalId)
                hechas.push(p.citaId)
            } catch (e) {
                nuevosFallos.push({
                    citaId: p.citaId,
                    motivo: e instanceof ErrorApi ? e.message : 'no se pudo mover',
                })
            }
        }

        // ⚠️ EN FORMA DE FUNCIÓN, Y NO `setPropuestas(quedan)`.
        //
        // `quedan` se derivaba de la copia capturada al empezar. Guardar tarda —
        // es una petición por cita— y cualquier cita arrastrada durante esos
        // segundos se perdía sin dejar rastro: el contador bajaba y el cambio no
        // se había escrito.
        setPropuestas((actuales) => {
            let n = actuales
            for (const id of hechas) n = sinPropuesta(n, id)
            return n
        })
        setFallos(nuevosFallos)
        setGuardando(false)
        await qc.invalidateQueries({ queryKey: ['calendario', accountId] })
    }

    return { propuestas, setPropuestas, guardando, fallos, setFallos, guardarPropuestas }
}
